/**
 * UseCase Filter Mapping Utilities
 *
 * This file contains utility functions for mapping and transforming
 * usecase filter configurations to the unified filter management system.
 *
 * The new usecase config uses filterKey instead of filter name,
 * and this utility bridges usecase selections to filterManagementSlice actions.
 */

import {getFilterConfig} from './filterManagementHelpers'

/**
 * Helper function to iterate over all useCases in the nested data structure
 * Reduces cognitive complexity by abstracting the 4-level deep traversal
 *
 * @param {Array} useCasesData - The full useCases array (domains > topics > subTopics > useCases)
 * @param {Function} callback - Function to call for each useCase
 */
export const forEachUseCase = (useCasesData, callback) => {
  if (!useCasesData) return

  for (const domain of useCasesData) {
    for (const topic of domain.topics || []) {
      for (const subTopic of topic.subTopics || []) {
        for (const useCase of subTopic.useCases || []) {
          callback(useCase)
        }
      }
    }
  }
}

/**
 * Check if a filter is dynamic (needs to fetch options from API)
 * Dynamic filters have empty options array in config
 *
 * @param {string} filterKey - The filter key to check
 * @param {Object} filterConfig - The filter configuration data
 * @returns {boolean} - True if filter is dynamic
 */
export const isDynamicFilter = (filterKey, filterConfig) => {
  const config = getFilterConfig(filterKey, filterConfig)

  if (!config) {
    return false
  }

  // Filter is dynamic if:
  // 1. Type is checkbox AND options is an empty array
  // 2. Has an identifier property (used for geographic filters)
  const hasEmptyOptions = Array.isArray(config.options) && config.options.length === 0
  const hasIdentifier = config.identifier !== undefined

  return hasEmptyOptions || hasIdentifier
}

/**
 * Transform usecase adjustedValue to filterManagement value format
 *
 * @param {any} adjustedValue - The adjusted value from usecase config
 * @param {string} filterType - The filter type (range, checkbox, toggle)
 * @param {Object} filterOptions - The filter options from config
 * @param {boolean} isDynamic - Whether this is a dynamic filter (options fetched from API)
 * @returns {any} - Transformed value for filterManagementSlice
 */
export const transformUseCaseValueToFilterValue = (
  adjustedValue,
  filterType,
  filterOptions = {},
  isDynamic = false
) => {
  if (adjustedValue === null || adjustedValue === undefined) {
    return null
  }

  switch (filterType) {
    case 'range':
      // Convert {min, max} to [min, max] array
      if (typeof adjustedValue === 'object' && 'min' in adjustedValue) {
        const min = adjustedValue.min
        let max
        if (adjustedValue.max !== null) {
          max = adjustedValue.max
        } else if (isDynamic) {
          max = null
        } else {
          max = filterOptions?.max ?? null
        }

        return [min, max]
      }
      return adjustedValue

    case 'checkbox':
      // Already array format, return as-is
      return Array.isArray(adjustedValue) ? adjustedValue : [adjustedValue]

    case 'toggle':
      // Already numeric (0, 1, or 2), return as-is
      return adjustedValue

    default:
      return adjustedValue
  }
}

/**
 * Get filters that should be added to drawer from usecase filters
 * Returns array of { key, categoryId, type } for addMultipleFilters action
 *
 * @param {Array} useCaseFilters - Array of filters from usecase config
 * @param {Object} filterConfig - The filter configuration data
 * @returns {Array} - Array of filter objects for addMultipleFilters
 */
export const getFiltersToAdd = (useCaseFilters, filterConfig) => {
  if (!useCaseFilters || !Array.isArray(useCaseFilters)) {
    return []
  }

  return useCaseFilters
    .map((filter) => {
      const filterKey = filter.filterKey
      const config = getFilterConfig(filterKey, filterConfig)

      if (!config) {
        console.warn(`Filter config not found for key: ${filterKey}`)
        return null
      }

      return {
        key: filterKey,
        categoryId: config.categoryId,
        type: config.type
      }
    })
    .filter(Boolean)
}

/**
 * Get filter values to apply from usecase filters
 * Excludes null adjustedValues (filters added but no value set)
 * Returns object { filterKey: value } for setMultipleFilterValues action
 *
 * @param {Array} useCaseFilters - Array of filters from usecase config
 * @param {Object} filterConfig - The filter configuration data
 * @returns {Object} - Object of { filterKey: transformedValue }
 */
export const getFilterValuesToApply = (useCaseFilters, filterConfig) => {
  if (!useCaseFilters || !Array.isArray(useCaseFilters)) {
    return {}
  }

  const filterValues = {}

  for (const filter of useCaseFilters) {
    const {filterKey, adjustedValue, type} = filter

    // Skip if no adjusted value (filter added but value not set)
    if (adjustedValue === null || adjustedValue === undefined) {
      continue
    }

    const config = getFilterConfig(filterKey, filterConfig)
    const filterOptions = config?.options

    // Check if this is a dynamic filter (needs to know for null max handling)
    const filterIsDynamic = isDynamicFilter(filterKey, filterConfig)

    const transformedValue = transformUseCaseValueToFilterValue(adjustedValue, type, filterOptions, filterIsDynamic)

    if (transformedValue !== null) {
      filterValues[filterKey] = transformedValue
    }
  }

  return filterValues
}

/**
 * Prepare all usecase filters for batch application
 * Returns array of { key, categoryId, type, value } objects
 *
 * @param {Array} useCaseFilters - Array of filters from usecase config
 * @param {Object} filterConfig - The filter configuration data
 * @returns {Array} - Array of prepared filter objects
 */
export const prepareUseCaseFiltersForApplication = (useCaseFilters, filterConfig) => {
  if (!useCaseFilters || !Array.isArray(useCaseFilters)) {
    return []
  }

  return useCaseFilters
    .map((filter) => {
      const {filterKey, adjustedValue, type} = filter
      const config = getFilterConfig(filterKey, filterConfig)

      if (!config) {
        console.warn(`Filter config not found for key: ${filterKey}`)
        return null
      }

      const transformedValue = transformUseCaseValueToFilterValue(adjustedValue, type, config.options)

      return {
        key: filterKey,
        categoryId: config.categoryId,
        type: config.type,
        value: transformedValue,
        hasValue: transformedValue !== null
      }
    })
    .filter(Boolean)
}

/**
 * Get all selected usecase filters from useCases data structure
 * (Used when multiple usecases are selected)
 *
 * @param {Array} useCasesData - The full useCases array (domains > topics > subTopics > useCases)
 * @returns {Array} - Array of all filters from selected usecases
 */
export const getSelectedUseCaseFilters = (useCasesData) => {
  const selectedFilters = []

  forEachUseCase(useCasesData, (useCase) => {
    if (useCase.selected && useCase.filters) {
      selectedFilters.push(...useCase.filters)
    }
  })

  return selectedFilters
}

/**
 * Merge checkbox filter values (union of arrays)
 */
const mergeCheckboxValues = (existing, adjustedValue) => {
  if (Array.isArray(existing.adjustedValue) && Array.isArray(adjustedValue)) {
    return [...new Set([...existing.adjustedValue, ...adjustedValue])]
  }
  return existing.adjustedValue
}

/**
 * Merge range filter values (most inclusive range)
 */
const mergeRangeValues = (existing, adjustedValue) => {
  if (!existing.adjustedValue || !adjustedValue) return existing.adjustedValue

  const minValue = Math.min(existing.adjustedValue.min, adjustedValue.min)
  const maxValue =
    existing.adjustedValue.max === null || adjustedValue.max === null
      ? null
      : Math.max(existing.adjustedValue.max, adjustedValue.max)

  return {min: minValue, max: maxValue}
}

/**
 * Merge two filter values based on filter type
 */
const mergeFilterValues = (existing, filter) => {
  const {type, adjustedValue} = filter

  switch (type) {
    case 'checkbox':
      return {...existing, adjustedValue: mergeCheckboxValues(existing, adjustedValue)}
    case 'range':
      return {...existing, adjustedValue: mergeRangeValues(existing, adjustedValue)}
    default:
      // Toggle and others: keep first value
      return existing
  }
}

/**
 * Merge filters from multiple selected usecases
 * When same filter appears in multiple usecases, combine their values
 *
 * @param {Array} filters - Array of all filters from selected usecases
 * @returns {Array} - Merged array with combined values for duplicate filterKeys
 */
export const mergeUseCaseFilters = (filters) => {
  const filterMap = new Map()

  for (const filter of filters) {
    const {filterKey, adjustedValue} = filter

    // First occurrence: add to map
    if (!filterMap.has(filterKey)) {
      filterMap.set(filterKey, {...filter})
      continue
    }

    // Skip if new value is null
    if (adjustedValue === null) continue

    const existing = filterMap.get(filterKey)

    // Replace if existing is null
    if (existing.adjustedValue === null) {
      filterMap.set(filterKey, {...filter})
      continue
    }

    // Merge based on type
    filterMap.set(filterKey, mergeFilterValues(existing, filter))
  }

  return Array.from(filterMap.values())
}

/**
 * Get filters that should be removed when a usecase is deselected
 *
 * @param {Array} currentFilters - Currently applied usecase filters
 * @param {Array} remainingFilters - Filters that will remain after deselection
 * @returns {Array} - Array of filterKeys to remove
 */
export const getFiltersToRemove = (currentFilters, remainingFilters) => {
  const remainingKeys = new Set(remainingFilters.map((f) => f.filterKey))
  return currentFilters.filter((f) => !remainingKeys.has(f.filterKey)).map((f) => f.filterKey)
}

/**
 * Check if a filterKey is used by any selected usecase
 *
 * @param {string} filterKey - The filter key to check
 * @param {Array} useCasesData - The full useCases array
 * @returns {boolean} - True if filterKey is used by a selected usecase
 */
export const isFilterKeyUsedBySelectedUseCase = (filterKey, useCasesData) => {
  const selectedFilters = getSelectedUseCaseFilters(useCasesData)
  return selectedFilters.some((f) => f.filterKey === filterKey)
}

/**
 * Get usecase IDs that use a specific filterKey
 *
 * @param {string} filterKey - The filter key to search for
 * @param {Array} useCasesData - The full useCases array
 * @returns {Array} - Array of usecase IDs that use this filter
 */
export const getUseCasesByFilterKey = (filterKey, useCasesData) => {
  const useCaseIds = []

  forEachUseCase(useCasesData, (useCase) => {
    const hasFilter = useCase.filters?.some((f) => f.filterKey === filterKey)
    if (hasFilter && useCase.selected) {
      useCaseIds.push(useCase.id)
    }
  })

  return useCaseIds
}

/* Function Descriptions:
 * - isDynamicFilter: Checks if a filter needs to fetch options dynamically from API
 * - transformUseCaseValueToFilterValue: Converts usecase adjustedValue format to filterManagement format
 * - getFiltersToAdd: Extracts filter objects for addMultipleFilters action from usecase config
 * - getFilterValuesToApply: Extracts filter values for setMultipleFilterValues action
 * - prepareUseCaseFiltersForApplication: Prepares complete filter data including transformed values
 * - getSelectedUseCaseFilters: Gets all filters from selected usecases in data structure
 * - mergeUseCaseFilters: Merges filters when multiple usecases select the same filter
 * - getFiltersToRemove: Identifies filters to remove when usecase is deselected
 * - isFilterKeyUsedBySelectedUseCase: Checks if a filter is used by any selected usecase
 * - getUseCasesByFilterKey: Gets usecase IDs that use a specific filter
 */
